import { Box, Chip, Typography } from '@mui/material';
import { format } from 'date-fns';
import PropTypes from 'prop-types';

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    return format(new Date(dateStr), 'MM/dd/yyyy');
  } catch {
    return dateStr;
  }
};

const formatQty = (qty) =>
  qty === null || qty === undefined ? '—' : Math.round(parseFloat(qty) || 0);

const ActiveAllocationsPanel = ({ allocations }) => (
  <Box>
    <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
      This item already has an active lot allocation. You can proceed to fulfillment using it, or
      discard it to select lots again.
    </Typography>

    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {allocations.map((allocation) => (
        <Box
          key={allocation.allocationId}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 1.5,
            '&:not(:last-of-type)': {
              borderBottom: '1px solid',
              borderColor: 'divider',
            },
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant='body2' fontWeight={600} noWrap>
              {allocation.lotNumber}
            </Typography>
            <Typography variant='caption' color='text.secondary' noWrap sx={{ display: 'block' }}>
              {[allocation.drugName, allocation.strength].filter(Boolean).join(' · ') || '—'}
              {allocation.ndc ? ` · NDC ${allocation.ndc}` : ''}
              {allocation.manufacturer ? ` · Manufacturer ${allocation.manufacturer}` : ''}
            </Typography>
            <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
              Exp {formatDate(allocation.bud)}
            </Typography>
          </Box>

          <Chip
            size='small'
            label={allocation.status}
            sx={{ height: 22, fontSize: '0.68rem', fontWeight: 600 }}
          />

          <Box sx={{ textAlign: 'right', minWidth: 72 }}>
            <Typography
              variant='caption'
              color='text.secondary'
              sx={{ display: 'block', lineHeight: 1.2 }}
            >
              Allocated
            </Typography>
            <Typography
              variant='body2'
              fontWeight={700}
              sx={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {formatQty(allocation.quantity)}
            </Typography>
          </Box>

          <Box sx={{ textAlign: 'right', minWidth: 72 }}>
            <Typography
              variant='caption'
              color='text.secondary'
              sx={{ display: 'block', lineHeight: 1.2 }}
            >
              Available
            </Typography>
            <Typography
              variant='body2'
              fontWeight={700}
              sx={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {formatQty(allocation.availableQty)}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  </Box>
);

ActiveAllocationsPanel.propTypes = {
  allocations: PropTypes.arrayOf(
    PropTypes.shape({
      allocationId: PropTypes.string.isRequired,
      lotNumber: PropTypes.string.isRequired,
      ndc: PropTypes.string,
      drugName: PropTypes.string,
      manufacturer: PropTypes.string,
      strength: PropTypes.string,
      quantity: PropTypes.number,
      status: PropTypes.string,
      bud: PropTypes.string,
      availableQty: PropTypes.number,
    })
  ).isRequired,
};

export default ActiveAllocationsPanel;
